/*
 * Web paths for the AI Course Builder (docs/course-builder/CONTRACTS.md, "Web
 * routes"). Every link in the feature is built here so a route change is one
 * edit, and so nothing interpolates an unchecked id into a path.
 */
export const ULID_RE = /^[0-9A-HJKMNP-TV-Z]{26}$/;

/* Slugs the cloud will never hand out, because they are route segments. */
export const RESERVED_SLUGS = ['new', 's', 'build', 'edit', 'lessons', 'admin', 'mine', 'community', 'shared', 'setup'];

export const HUB_TABS = ['courses', 'community', 'mine', 'shared'];

export function isUlid(v) {
  return typeof v === 'string' && ULID_RE.test(v);
}

/* A course slug as the cloud makes them: lowercase words joined by hyphens. */
export function isCourseSlug(v) {
  return typeof v === 'string'
    && /^[a-z0-9](?:[a-z0-9-]{0,118}[a-z0-9])?$/.test(v)
    && !RESERVED_SLUGS.includes(v)
    && !isUlid(v.toUpperCase());
}

const seg = (v) => encodeURIComponent(String(v ?? ''));

export const paths = {
  hub: (tab) => (tab && HUB_TABS.includes(tab) ? `/courses?tab=${tab}` : '/courses'),
  mine: () => '/courses?tab=mine',
  newCourse: () => '/courses/ai/new',
  course: (idOrSlug) => `/courses/ai/${seg(idOrSlug)}`,
  lesson: (idOrSlug, lessonId) => `/courses/ai/${seg(idOrSlug)}/lessons/${seg(lessonId)}`,
  quiz: (idOrSlug, moduleId) => `/courses/ai/${seg(idOrSlug)}?view=quiz&module=${seg(moduleId)}`,
  capstone: (idOrSlug) => `/courses/ai/${seg(idOrSlug)}?view=capstone`,
  print: (idOrSlug) => `/courses/ai/${seg(idOrSlug)}?view=print`,
  build: (courseId) => `/courses/ai/${seg(courseId)}/build`,
  edit: (courseId, tab) => `/courses/ai/${seg(courseId)}/edit${tab ? `?tab=${seg(tab)}` : ''}`,
  link: (token) => `/courses/ai/s/${seg(token)}`,
  linkLesson: (token, lessonId) => `/courses/ai/s/${seg(token)}/lessons/${seg(lessonId)}`,
};

/*
 * A path that came from the server (a notification's `path`). Only an
 * in-app absolute path is followed: no scheme, no protocol-relative `//host`,
 * no backslash tricks. Anything else is dropped.
 */
export function safeAppPath(p) {
  if (typeof p !== 'string') return null;
  const s = p.trim();
  if (!s.startsWith('/') || s.startsWith('//') || s.includes('\\')) return null;
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f\s]/.test(s)) return null;
  return s;
}

/* The link builder for "in this course" pages, for both id and link modes. */
export function coursePaths({ courseKey, token }) {
  if (token) {
    return {
      overview: () => paths.link(token),
      lesson: (lid) => paths.linkLesson(token, lid),
      quiz: (mid) => `${paths.link(token)}?view=quiz&module=${seg(mid)}`,
      capstone: () => `${paths.link(token)}?view=capstone`,
      print: () => `${paths.link(token)}?view=print`,
    };
  }
  return {
    overview: () => paths.course(courseKey),
    lesson: (lid) => paths.lesson(courseKey, lid),
    quiz: (mid) => paths.quiz(courseKey, mid),
    capstone: () => paths.capstone(courseKey),
    print: () => paths.print(courseKey),
  };
}
